import type { FC } from "react";

import { useDispatch } from "react-redux";

import { QuickActionCard } from "app/pages/sales-intelligence/modules/suggestions/components/QuickActionCard/QuickActionCard";
import { QuickActionLoadingCard } from "app/pages/sales-intelligence/modules/suggestions/components/QuickActionLoadingCard/QuickActionLoadingCard";
import { getSuggestionsQuickActions } from "app/pages/sales-intelligence/modules/suggestions/constants";
import { StyledQuickActionsContainer } from "app/pages/sales-intelligence/modules/suggestions/containers/QuickActionsContainer/styles";
import { SuggestionQuickActionType } from "app/pages/sales-intelligence/modules/suggestions/types/enums";
import {
    getSuggestedAction,
    sortByConfigOrder,
} from "app/pages/sales-intelligence/modules/suggestions/utils/suggested-actions";
import { toggleOpportunityListModal } from "app/pages/sales-intelligence/sub-modules/opportunities/store/action-creators";

import trackingService from "../../services/trackingService/trackingService";

const LOADING_CARD_AMOUNT = 3;

interface QuickActionsContainerProps {
    suggestedActions: readonly string[];
    isLoading: boolean;
}

export const QuickActionsContainer: FC<QuickActionsContainerProps> = ({ suggestedActions, isLoading }) => {
    const dispatch = useDispatch();

    const customActions = {
        [SuggestionQuickActionType.CreateList]: () => dispatch(toggleOpportunityListModal(true)),
    };

    const handleOnClick = (type: SuggestionQuickActionType, func?: VoidFunction): VoidFunction => {
        return () => {
            trackingService.trackClickQuickAction(type);
            func?.();
        };
    };

    if (isLoading) {
        return (
            <StyledQuickActionsContainer>
                {Array.from({ length: LOADING_CARD_AMOUNT }, (_, index) => (
                    <QuickActionLoadingCard key={index} />
                ))}
            </StyledQuickActionsContainer>
        );
    }

    if (suggestedActions.length === 0 && !isLoading) {
        return null;
    }

    const suggestionsQuickActions = getSuggestionsQuickActions();

    return (
        <StyledQuickActionsContainer>
            {sortByConfigOrder(suggestedActions)
                .slice(0, 3)
                .map((item: string) => {
                    const suggestion = suggestionsQuickActions[item];
                    const onActionClick = getSuggestedAction(suggestion.type, customActions);
                    return (
                        <QuickActionCard
                            {...suggestion}
                            onClick={handleOnClick(suggestion.type, onActionClick)}
                            dataAutomation={suggestion.dataAutomation}
                        />
                    );
                })}
        </StyledQuickActionsContainer>
    );
};
